/**
 * Декодер росчерка (06.09, владелец) — SHARK2-подобный, без внешних библиотек.
 *
 * Путь пальца ресэмплируется до N равноотстоящих точек по длине дуги; для
 * каждого слова-кандидата (dict.index по первой+последней букве, с соседями
 * стартовой/конечной клавиш) строится «идеальная» траектория по центрам клавиш
 * и сравнивается по форме (центроид в 0, масштаб к единичному размаху) и по
 * положению (в ширинах клавиши); плюс штраф за длину (уникальные клавиши vs
 * повороты+2), мягкий штраф за пропущенные буквы поворотов и частота.
 * score — меньше = лучше. Горячий цикл без аллокаций: буферы Float64Array
 * переиспользуются между кандидатами.
 */
import type { GlideCandidate, GlideDict, GlidePoint, KeyBox } from "./types.ts";

const N = 48;
const POOL = 3000;
const MAX_WORD = 64;
const COS_TURN = 0.5; // cos 60°
const TURN_K = 3; // полуокно оценки направления (в ресэмплированных точках)

const W_SHAPE = 1.0;
const W_LOC = 0.6;
const W_LEN = 0.15;
const W_TURN = 0.1;
const W_FREQ = 0.12;

// Переиспользуемые буферы (декодер синхронный, реентерабельность не нужна).
const userBuf = new Float64Array(2 * N); // ресэмплированный путь пальца
const userNorm = new Float64Array(2 * N); // тот же путь, форма-нормализованный
const wordSrc = new Float64Array(2 * MAX_WORD); // центры клавиш слова
const wordBuf = new Float64Array(2 * N); // слово, ресэмплированное до N
const wordKeys = new Int16Array(MAX_WORD);
let seen = new Uint8Array(0); // флаги «клавиша уже учтена» (по индексу клавиши)

function nearestKey(keys: KeyBox[], x: number, y: number): number {
  let best = -1;
  let bd = Infinity;
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i]!;
    const dx = k.cx - x;
    const dy = k.cy - y;
    const d = dx * dx + dy * dy;
    if (d < bd) { bd = d; best = i; }
  }
  return best;
}

/** Ресэмплинг ломаной src (m точек, interleaved x,y) в out (ровно N точек). Возвращает длину дуги. */
function resample(src: Float64Array, m: number, out: Float64Array): number {
  let total = 0;
  for (let i = 1; i < m; i++) {
    const dx = src[2 * i]! - src[2 * i - 2]!;
    const dy = src[2 * i + 1]! - src[2 * i - 1]!;
    total += Math.sqrt(dx * dx + dy * dy);
  }
  if (m < 2 || total === 0) {
    for (let k = 0; k < N; k++) { out[2 * k] = src[0]!; out[2 * k + 1] = src[1]!; }
    return 0;
  }
  const step = total / (N - 1);
  let seg = 0;
  let segStart = 0;
  let segLen = Math.hypot(src[2]! - src[0]!, src[3]! - src[1]!);
  for (let k = 0; k < N; k++) {
    const s = k === N - 1 ? total : k * step;
    while (seg < m - 2 && segStart + segLen < s) {
      segStart += segLen;
      seg++;
      segLen = Math.hypot(src[2 * seg + 2]! - src[2 * seg]!, src[2 * seg + 3]! - src[2 * seg + 1]!);
    }
    let t = segLen > 0 ? (s - segStart) / segLen : 0;
    if (t < 0) t = 0; else if (t > 1) t = 1;
    const x0 = src[2 * seg]!;
    const y0 = src[2 * seg + 1]!;
    out[2 * k] = x0 + (src[2 * seg + 2]! - x0) * t;
    out[2 * k + 1] = y0 + (src[2 * seg + 3]! - y0) * t;
  }
  return total;
}

/** Центроид в 0, масштаб к единичному размаху (макс. сторона bounding-box). */
function normalizeShape(src: Float64Array, out: Float64Array): void {
  let sx = 0, sy = 0, minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (let k = 0; k < N; k++) {
    const x = src[2 * k]!, y = src[2 * k + 1]!;
    sx += x; sy += y;
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  }
  const cx = sx / N, cy = sy / N;
  const span = Math.max(maxX - minX, maxY - minY) || 1;
  for (let k = 0; k < N; k++) {
    out[2 * k] = (src[2 * k]! - cx) / span;
    out[2 * k + 1] = (src[2 * k + 1]! - cy) / span;
  }
}

/** Индексы точек-поворотов (направление меняется > 60°), серии соседних поворотов схлопнуты в одну. */
function findTurns(p: Float64Array): number[] {
  const turns: number[] = [];
  let inTurn = false, bestI = -1, bestCos = 1;
  for (let i = TURN_K; i < N - TURN_K; i++) {
    const ax = p[2 * i]! - p[2 * (i - TURN_K)]!, ay = p[2 * i + 1]! - p[2 * (i - TURN_K) + 1]!;
    const bx = p[2 * (i + TURN_K)]! - p[2 * i]!, by = p[2 * (i + TURN_K) + 1]! - p[2 * i + 1]!;
    const la = Math.sqrt(ax * ax + ay * ay), lb = Math.sqrt(bx * bx + by * by);
    const cos = la > 1e-9 && lb > 1e-9 ? (ax * bx + ay * by) / (la * lb) : 1;
    if (cos < COS_TURN) {
      if (!inTurn || cos < bestCos) { bestI = i; bestCos = cos; }
      inTurn = true;
    } else if (inTurn) {
      turns.push(bestI); inTurn = false; bestCos = 1;
    }
  }
  if (inTurn) turns.push(bestI);
  return turns;
}

/** Индексы клавиш, центры которых ближе 1.2·w к центру клавиши at (включая её саму). */
function neighbors(keys: KeyBox[], at: number): number[] {
  const k = keys[at]!;
  const r2 = (1.2 * k.w) ** 2;
  const out: number[] = [];
  for (let i = 0; i < keys.length; i++) {
    const o = keys[i]!;
    if ((o.cx - k.cx) ** 2 + (o.cy - k.cy) ** 2 <= r2) out.push(i);
  }
  return out;
}

/** Буквы клавиш под траекторией (ближайшая к каждой точке, повторы подряд схлопнуты). */
export function rawKeySequence(path: GlidePoint[], keys: KeyBox[]): string {
  let out = "";
  let prev = -1;
  for (const pt of path) {
    const i = nearestKey(keys, pt.x, pt.y);
    if (i < 0 || i === prev) continue;
    out += keys[i]!.ch;
    prev = i;
  }
  return out;
}

export function decodeGlide(path: GlidePoint[], keys: KeyBox[], dict: GlideDict, limit = 4): GlideCandidate[] {
  if (path.length === 0 || keys.length === 0 || limit <= 0) return [];
  const kw = keys[0]!.w;
  const src = new Float64Array(2 * path.length);
  for (let i = 0; i < path.length; i++) { src[2 * i] = path[i]!.x; src[2 * i + 1] = path[i]!.y; }
  if (resample(src, path.length, userBuf) < 0.5 * kw) return [];
  normalizeShape(userBuf, userNorm);

  const turns = findTurns(userBuf);
  const startKey = nearestKey(keys, userBuf[0]!, userBuf[1]!);
  const endKey = nearestKey(keys, userBuf[2 * N - 2]!, userBuf[2 * N - 1]!);
  let turnChars = "";
  for (const ti of turns) {
    const ki = nearestKey(keys, userBuf[2 * ti]!, userBuf[2 * ti + 1]!);
    if (ki !== startKey && ki !== endKey) turnChars += keys[ki]!.ch;
  }
  const expectedKeys = turns.length + 2;

  // Пул кандидатов: пары (первая, последняя) по соседям старта/финиша, обрезка по рангу.
  const firsts = neighbors(keys, startKey);
  const lasts = neighbors(keys, endKey);
  let pool: number[] = [];
  for (const f of firsts) for (const l of lasts) {
    const ranks = dict.index.get(keys[f]!.ch + keys[l]!.ch);
    if (ranks) pool = pool.concat(ranks);
  }
  if (pool.length === 0) return [];
  const ranked = Int32Array.from(pool).sort();
  const poolN = Math.min(ranked.length, POOL);

  const keyOf = new Map<string, number>();
  for (let i = 0; i < keys.length; i++) keyOf.set(keys[i]!.ch, i);
  if (seen.length < keys.length) seen = new Uint8Array(keys.length);

  // Топ-K (K = 2·limit с запасом на дубли слов), отсортированный по score.
  const K = limit * 2;
  const topRank = new Int32Array(K);
  const topScore = new Float64Array(K).fill(Infinity);
  let topN = 0;

  for (let c = 0; c < poolN; c++) {
    const rank = ranked[c]!;
    const word = dict.words[rank];
    if (!word || word.length < 2) continue;
    let m = 0;
    let uniq = 0;
    for (let i = 0; i < word.length && m < MAX_WORD; i++) {
      const ki = keyOf.get(dict.norm(word[i]!));
      if (ki === undefined) continue;
      const k = keys[ki]!;
      wordSrc[2 * m] = k.cx; wordSrc[2 * m + 1] = k.cy;
      wordKeys[m++] = ki;
      if (seen[ki] === 0) { seen[ki] = 1; uniq++; }
    }
    for (let i = 0; i < m; i++) seen[wordKeys[i]!] = 0;
    if (m < 1) continue;

    resample(wordSrc, m, wordBuf);
    // Один проход: центроид и размах слова.
    let sx = 0, sy = 0, minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (let k = 0; k < N; k++) {
      const x = wordBuf[2 * k]!, y = wordBuf[2 * k + 1]!;
      sx += x; sy += y;
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    const cx = sx / N, cy = sy / N;
    const inv = 1 / (Math.max(maxX - minX, maxY - minY) || 1);
    // Второй проход: shape (нормализованные) и location (сырые / w) дистанции.
    let shape = 0, loc = 0;
    for (let k = 0; k < N; k++) {
      const wx = wordBuf[2 * k]!, wy = wordBuf[2 * k + 1]!;
      const ux = userBuf[2 * k]!, uy = userBuf[2 * k + 1]!;
      const dsx = (wx - cx) * inv - userNorm[2 * k]!, dsy = (wy - cy) * inv - userNorm[2 * k + 1]!;
      shape += Math.sqrt(dsx * dsx + dsy * dsy);
      loc += Math.sqrt((wx - ux) * (wx - ux) + (wy - uy) * (wy - uy));
    }
    shape /= N;
    loc /= N * kw;

    let missing = 0;
    for (let i = 0; i < turnChars.length; i++) if (!word.includes(turnChars[i]!)) missing++;
    const score =
      W_SHAPE * shape + W_LOC * loc +
      W_LEN * Math.abs(uniq - expectedKeys) + W_TURN * missing +
      W_FREQ * Math.log(1 + rank / 500);

    if (score >= topScore[K - 1]!) continue;
    let pos = topN < K ? topN : K - 1;
    while (pos > 0 && topScore[pos - 1]! > score) {
      topScore[pos] = topScore[pos - 1]!; topRank[pos] = topRank[pos - 1]!; pos--;
    }
    topScore[pos] = score; topRank[pos] = rank;
    if (topN < K) topN++;
  }

  const out: GlideCandidate[] = [];
  const words = new Set<string>();
  for (let i = 0; i < topN && out.length < limit; i++) {
    const word = dict.words[topRank[i]!]!;
    if (words.has(word)) continue;
    words.add(word);
    out.push({ word, score: topScore[i]! });
  }
  return out;
}
